/* @pareto-engineering/generator-front 1.0.12 */
import * as React from 'react'

import {
  Quote,
  useApp,
} from '@pareto-engineering/design-system'

import {
  CaseStudyPage,
} from 'modules/components'

import * as MODULE_URLS from 'modules/urls'

const headline = 'Advancing AI Alignment through Human-Judged LLM Debates'
const mainImage = '/casestudies/MATS.webp'
const url = MODULE_URLS.CASE_STUDIES.MATS

const MATS = () => {
  const { SITE: { CANONICAL } } = useApp()

  return (
    <CaseStudyPage
      jsonld={{
        '@context'      :'https://schema.org',
        '@type'         :'Article',
        mainEntityOfPage:{
          '@type':'WebPage',
          '@id'  :`${CANONICAL}${url}`,
        },
        headline,
        image        :`${CANONICAL}${mainImage}`,
        datePublished:'2024-04-05',
        dateModified :'2024-04-05',
      }}
      id="debate-judgement-mats"
      metaTitle="Case Study | Advancing AI Alignment through Human-Judged LLM Debates"
      metaDescription="Pareto is proud to collaborate with researchers from MATS, University College London, and Anthropic by orchestrating human-judged debates among LLMs."
      title={headline}
      subtitle="Learn how Pareto helped MATS obtain high quality data for their research"
      initialProp="Companies"
      initialValue="MATS, UC London, and Anthropic"
      secondaryValue="Large Language Models"
      mainImage={mainImage}
      ctaTitle={(
        <>
          Need help with LLM development,
          {' '}
          <span className="no-wrap">fine-tuning,</span>
          {' '}
          or research?
        </>
      )}
      ctaDescription="Reach out to discover how we can help you obtain premium-quality data for your projects."
      ctaButtonText="Get started"
    >
      <p className="h1">
        Overview
      </p>
      <p>
        The ML Alignment & Theory Scholars (MATS) Program, together with researchers from
        Anthropic and University College London needed high-quality data to test scalable AI
        oversight mechanisms for ensuring AI safety and alignment.
      </p>
      <p>
        The project demanded a highly-skilled AI training workforce to judge debates between
        language models, adaptable task guidelines to reflect the iterative nature of research,
        and direct collaboration channels for quick feedback between researchers and workers.
      </p>
      <p>
        Pareto.AI assisted the consortium of researchers in collecting high-quality data from
        human-judged debates between LLMs.
      </p>
      <p className="h1">
        Our advantage: Why they chose Pareto.AI
      </p>
      <Quote
        author="Dan Valentine"
        className="manual span-7"
      >
        We first tried to set this up with other crowd work providers. It took weeks to even get
        on a zoom call with them, and the call made it clear that they didn&apos;t have the
        flexibility to meet our needs. Pareto.AI, on the other hand, got on a call with us within
        24 hours of us first reaching out, came up with a plan tailored for our needs, and we were
        running our first experiment with them within a couple of weeks.
      </Quote>
      <p>
        The researchers ran into significant problems finding the right partner for this project.
        Crowd work providers were hesitant to embrace the project&apos;s complexity,
        particularly its need for iterative weekly changes, customized workflows, and direct
        communication with labelers during the project.
      </p>
      <p>
        Some notable providers in this space demonstrated a lack of responsiveness, taking over
        a month to reply to initial inquiries and showing reluctance to facilitate direct
        communication between the researchers and the workers. Their disinterest in supporting a
        project of this size and complexity revealed a gap between the researchers&apos;
        needs and what most data labeling vendors could offer.
      </p>
      <p>
        Existing crowd work platforms were not built to handle projects of this nature, as they
        are primarily designed for high-volume, repeatable tasks, making them unsuitable for
        projects that require nuanced understanding, expert judgment, or the ability to rapidly
        iterate based on evolving guidelines.
      </p>
      <p>
        Additionally, most existing systems are structured around minimizing direct contact with
        workers to simplify management and reduce overhead, which unfortunately can lead to
        misunderstandings, generic feedback, and a slower resolution of issues. For a project of
        this nature, the lack of direct engagement and adaptability could hinder its success.
      </p>
      <p className="h1">
        Solution
      </p>
      <p>
        Pareto sourced, onboarded, and trained 20 experts in less than a month
        through
        {' '}
        <span className="emphasis">referral-based sourcing</span>
        , leveraging the strength of our extensive
        network of highly-skilled workers.
      </p>
      <p>
        We secured and retained top-tier talent by committing upfront to guaranteed working hours
        for a carefully vetted group of expert workers over an extended period. We also
        implemented a thorough testing and qualification process, which included a week of feedback.
      </p>
      <p>
        The data collection project involved
        {' '}
        <span className="emphasis">judging debates between LLM responses</span>
        , where the goal was to choose the correct answer to a question presented
        in the debate. Pareto oversaw the data collection process from start to finish,
        providing daily updates on debate judgements from labelers and ensuring adherence
        to quality standards.
      </p>
      <p>
        Bugs and high latency were identified as significant issues that could impact workers&apos;
        performance. To address this, we proactively aided researchers in implementing a
        {' '}
        <span className="emphasis">robust error recovery system</span>
        {' '}
        and conducted thorough testing of the platform before its rollout to workers.
        This was made possible because our system is intentionally designed to accommodate
        quick pivots and adjustments.
      </p>
      <p>
        To facilitate collaboration between workers and requesters, Pareto established
        {' '}
        <span className="emphasis">
          direct communication channels
        </span>
        , enabling real-time interaction between researchers
        and workers.
      </p>
      <p>
        Pareto&apos;s model, by prioritizing open dialogue and immediate feedback, fostered
        a more collaborative and adaptive environment that traditional platforms cannot
        easily implement for such projects.
      </p>
      <Quote
        author="Dan Valentine"
        className="manual span-7"
      >
        The workers Pareto provided were fantastic, super communicative and responsive, and very
        skilled at this unusual, difficult task. We required a written explanation with each
        label - I&apos;ve read hundreds of these, and am amazed at the consistently high quality
        and attention to detail provided
        throughout the experiments.
      </Quote>
      <p className="h1">
        Results
      </p>
      <Quote
        author="Dan Valentine"
        className="manual span-7"
      >
        Working with Pareto was crucial to the success of our human experiments.
        They&apos;ll be my first choice for any crowd work we need in the future.
      </Quote>
      <p>
        As a result of Pareto&apos;s high-quality, comprehensive data,
        researchers from MATS, Anthropic,
        and UCL were able to derive crucial insights:
      </p>
      <ul>
        <li>
          As language models get more capable, debating with LLM responses enables scalable
          oversight by non-expert human evaluators.
        </li>
        <li>
          Language models optimized for &quot;judge approval&quot; become more truthful in
          the process of debates. In other words, debating with persuasive LLMs
          lead to more truthful answers.
        </li>
        <li>
          Debate with language models leads to higher judge accuracy than consultancy.
        </li>
      </ul>
      <p>
        Powerful insights like these allow MATS, Anthropic, and UCL researchers to better
        understand where they need to focus their efforts next. Furthermore, these results
        pave the way for future research on adversarial oversight methods and protocols
        that enable non-experts to elicit truth from sophisticated language models.
      </p>
      <Quote
        author="Akbir Khan"
        className="manual span-7"
      >
        Working with Pareto was great. They understood our problem immediately,
        sourced high-quality annotators and understood the importance of rigorous human evaluation.
      </Quote>
      <p>
        You can read MATS&apos; research paper on the viability of aligning AI models with debate
        in the absence of ground truth
        {' '}
        <a href="https://arxiv.org/abs/2402.06782" target="_blank" rel="noreferrer">
          here
        </a>
        .
      </p>
    </CaseStudyPage>
  )
}

export default MATS
